import { useEffect } from "react";
import { useLocation } from "wouter";

/**
 * URL REDIRECT MAP
 * Maps all old integrated.social URLs to new site structure.
 * This prevents 404 errors for pages already indexed by Google.
 * 
 * Old site structure (Squarespace):
 * - /ai-marketing-aeo-geo-blog → Blog listing
 * - /ai-marketing-aeo-geo-blog/{slug} → Individual blog posts
 * - /ai-marketing-aeo-geo-blog/category/{name} → Category pages
 * - /ai-marketing-aeo-geo-blog/tag/{name} → Tag pages
 * - /ai-seo-aeo-geo-aio-agency-london → SEO service page
 * - /ppc-performance-max-agency-london → PPC service page
 * - /ai-marketing-strategy-genai-content-ops-london → Strategy page
 * - /free-ai-growth-audit → AuditPage (interactive AI Visibility Audit tool - no redirect)
 * - /modi-elnadi → About page
 * - /home → Homepage
 * - /store → (deprecated)
 */

// Blog slug mapping: old Squarespace slugs → new slugs
const BLOG_SLUG_MAP: Record<string, string> = {
  // Direct matches (same slug)
  "moltbook-ai-only-social-network-explained": "moltbook-ai-only-social-network-explained",
  "openai-ipo-hurdles-vs-anthropic": "openai-ipo-hurdles-vs-anthropic",
  "top-10-ai-startups-in-uk-2026": "top-10-ai-startups-uk-2026",
  
  // Slug variations (old → new)
  "voice-search-isnt-dead-apples-audio-ai-move-and-the-return-of-spoken-queries": "voice-search-apple-qai-acquisition",
  "breaking-nano-banana-button-added-to-google-search-mobile-app-today": "nano-banana-google-search-button",
  "ai-replacing-jobs-what-you-need-to-know-today": "ai-replacing-jobs-what-you-need-to-know",
  "chatgpt-ads-are-coming": "chatgpt-ai-ads-coming",
  
  // Old posts that map to closest new equivalent
  "openais-chatgpt-o1-model-is-out-the-future-of-ai-reasoning-is-here": "gpt-5-6-openai-release-cadence-marketers",
  "future-of-ai-ppc-london-agencies-new-googles-performance-max-campaigns": "chatgpt-ai-ads-coming",
  "the-ai-future-is-now-embrace-ai-but-prepare-for-job-reskill-or-displacement-pt-1": "ai-replacing-jobs-what-you-need-to-know",
  "the-dark-side-of-micro-management": "multi-agent-ai-automate-gtm-2026",
  "chinese-hackers-exploit-testflight-to-distribute-malicious-apps-beta-invitation-code-scam": "ai-governance-2026-quality-control",
  "how-to-stop-chrome-iphone-account-takeovers-a-step-by-step-security-guide-for-google-and-apple-users": "ai-governance-2026-quality-control",
  "latest-drone-uap-incursions-in-the-usa-and-uk": "spacex-orbital-data-centers-ai-marketing-infrastructure",
  "vudgmoiazgg0tmnjp0vowwtc5o8z6a": "", // spam/broken slug → blog listing
  // Slugs flagged in GSC duplicate-meta report (2026-07-01)
  "ai-future-is-now-ai-impact-on-creative-industries-pt-3": "ai-replacing-jobs-what-you-need-to-know",
  // Session 34 - 404 fixes
  "ai-governance-2026-prove-quality-qa-gates-audit-trail": "ai-governance-2026-quality-control",
};

// Category → tag mapping for the new site
const CATEGORY_TAG_MAP: Record<string, string> = {
  "AGI": "AI Agents",
  "AGI / AI Strategy": "AI Marketing Strategy",
  "AI Agents": "AI Agents",
  "AI Answers optimization": "AI Answers & AEO",
  "AI Answers Optimisation": "AI Answers & AEO",
  "AI+Answers+Optimisation": "AI Answers & AEO",
  "AI Benchmarks": "Tech News & Trends",
  "AI PPC": "PPC & Performance Max",
  "AI Startups": "AI Startups & Funding",
  "AI Startups, Funding & Markets": "AI Startups & Funding",
  "Apple": "Tech News & Trends",
  "Apple iOS": "Tech News & Trends",
  "B2B SaaS": "AI Marketing Strategy",
  "Chrome": "Tech News & Trends",
  "Creative Ops": "AI Marketing Strategy",
  "Cybersecurity": "AI Governance & Safety",
  "EU Regulation": "AI Governance & Safety",
  "Ecommerce": "Ecommerce & AI Commerce",
  "Entity SEO": "AI Search (SEO/GEO)",
  "Funding & Markets": "AI Startups & Funding",
  "Future": "Future of Work",
  "Future of Work & Jobs": "Future of Work",
  "GEO": "AI Search (SEO/GEO)",
  "Google": "Google AI Overviews",
  "Google AI Mode": "Google AI Overviews",
  "Google AI Overviews": "Google AI Overviews",
  "How To Guide": "Digital Marketing Tips",
  "Innovation": "Tech News & Trends",
  "Jobs": "Future of Work",
  "LLM": "AI Agents",
  "LLM citation strategies": "AI Answers & AEO",
  "News": "Tech News & Trends",
  "OpenAi": "Tech News & Trends",
  "Paid Media (PPC & Performance Max)": "PPC & Performance Max",
  "Safety": "AI Governance & Safety",
  "Safety & Security": "AI Governance & Safety",
  "Security": "AI Governance & Safety",
  "Tech": "Tech News & Trends",
  "Technical SEO": "AI Search (SEO/GEO)",
  "Tips": "Digital Marketing Tips",
  "Trending": "Tech News & Trends",
  "VC Funding": "AI Startups & Funding",
  "VCs": "AI Startups & Funding",
  "Voice": "AI Answers & AEO",
};

// Tag → tag mapping (old tags to new tags)
const TAG_MAP: Record<string, string> = {
  "ai answers optimization": "AI Answers & AEO",
  "ai jobs": "Future of Work",
  "ai seo best practices": "AI Search (SEO/GEO)",
  "ai startups": "AI Startups & Funding",
  "answer engine optimization": "AI Answers & AEO",
  "chatgpt": "AI Agents",
  "eu ai regulation": "AI Governance & Safety",
  "generative engine optimization": "AI Search (SEO/GEO)",
  "google ads": "PPC & Performance Max",
  "google ai overviews + gemini ai search": "Google AI Overviews",
  "google-ai-overviews-gemini-ai-search": "Google AI Overviews",
  "google ai overviews  gemini ai search": "Google AI Overviews",
  "ipo": "AI Startups & Funding",
  "llm optimization": "AI Answers & AEO",
  "london agency": "AI Marketing Strategy",
  "openai": "Tech News & Trends",
  "paid search (ppc)": "PPC & Performance Max",
  "security & safety": "AI Governance & Safety",
  "security and safety": "AI Governance & Safety",
  "tech news": "Tech News & Trends",
  "tech trends": "Tech News & Trends",
  "venture capital (vc)": "AI Startups & Funding",
  "Comparison": "Tech News & Trends",
  "Compliance": "AI Governance & Safety",
  "Conversion Rate (CVR)": "PPC & Performance Max",
  "Creative Governance": "AI Marketing Strategy",
  "Creative Ops": "AI Marketing Strategy",
  "Cybersecurity": "AI Governance & Safety",
  "Data Governance": "AI Governance & Safety",
  "EU Regulation": "AI Governance & Safety",
  "Ecommerce": "Ecommerce & AI Commerce",
  "Entity SEO": "AI Search (SEO/GEO)",
  "Evaluation Methodology": "Tech News & Trends",
  "Explainer": "Digital Marketing Tips",
  "Feed Optimization": "PPC & Performance Max",
  "Forecast": "Tech News & Trends",
  "Google AI Mode": "Google AI Overviews",
  "Healthcare": "AI Marketing Strategy",
  "How-to": "Digital Marketing Tips",
  "Incrementality": "PPC & Performance Max",
  "LLMO": "AI Answers & AEO",
  "Market Stats": "Tech News & Trends",
  "Measurement Design": "PPC & Performance Max",
  "Measurement Setup": "PPC & Performance Max",
  "Meta": "Tech News & Trends",
  "Performance Max": "PPC & Performance Max",
  "Policy Impact": "AI Governance & Safety",
  "ROAS/CPA": "PPC & Performance Max",
  "Rank & Visibility": "AI Search (SEO/GEO)",
  "Risk Reduction": "AI Governance & Safety",
  "Schema Markup": "AI Search (SEO/GEO)",
  "Security Hardening": "AI Governance & Safety",
  "Share of Citations": "AI Answers & AEO",
  "Threat Modeling": "AI Governance & Safety",
  "Tips": "Digital Marketing Tips",
  "Top 10": "Tech News & Trends",
  "UK Market": "AI Startups & Funding",
  "VC Funding": "AI Startups & Funding",
  "Workflow Redesign": "Agentic AI",
};

export default function Redirects() {
  const [location, setLocation] = useLocation();

  useEffect(() => {
    const path = location;

    // 1. Service pages → dedicated service landing pages
    if (path === "/ai-seo-aeo-geo-aio-agency-london") {
      setLocation("/services/seo-aeo-geo");
      return;
    }
    if (path === "/ppc-performance-max-agency-london") {
      setLocation("/services/ppc-performance-max");
      return;
    }
    if (path === "/ai-marketing-strategy-genai-content-ops-london") {
      setLocation("/services/ai-marketing-strategy");
      return;
    }

    // 1b. Location page legacy URLs → canonical service pages
    if (path === "/ai-marketing-agency-london" || path === "/ai-marketing-agency-london/") {
      setLocation("/services/ai-marketing-strategy/");
      return;
    }
    if (path === "/ai-website-agency-london" || path === "/ai-website-agency-london/") {
      setLocation("/services/ai-websites/");
      return;
    }
    if (path === "/marketing-agency-wimbledon" || path === "/marketing-agency-wimbledon/") {
      setLocation("/services/");
      return;
    }

    // 3b. /results/ → /case-studies/
    if (path === "/results" || path === "/results/") {
      setLocation("/case-studies/");
      return;
    }

    // 4. /home → /
    if (path === "/home") {
      setLocation("/");
      return;
    }

    // 5. /store → / (deprecated)
    if (path === "/store") {
      setLocation("/");
      return;
    }

    // 6. Blog listing
    if (path === "/ai-marketing-aeo-geo-blog") {
      setLocation("/blog");
      return;
    }

    // 7. Blog category pages → blog with tag filter
    const categoryMatch = path.match(/^\/ai-marketing-aeo-geo-blog\/category\/(.+)$/);
    if (categoryMatch) {
      // Try exact match first, then normalise plus signs to spaces for legacy Squarespace URLs
      const rawCategory = decodeURIComponent(categoryMatch[1]);
      const normCategory = rawCategory.replace(/\+/g, " ");
      const categoryName = CATEGORY_TAG_MAP[rawCategory] ? rawCategory : normCategory;
      const mappedTag = CATEGORY_TAG_MAP[categoryName] || "Tech News & Trends";
      setLocation(`/blog?tag=${encodeURIComponent(mappedTag)}`);
      return;
    }

    // 8. Blog tag pages → blog with tag filter
    const tagMatch = path.match(/^\/ai-marketing-aeo-geo-blog\/tag\/(.+)$/);
    if (tagMatch) {
      const tagName = decodeURIComponent(tagMatch[1]);
      const mappedTag = TAG_MAP[tagName] || "Tech News & Trends";
      setLocation(`/blog?tag=${encodeURIComponent(mappedTag)}`);
      return;
    }

    // 10. Broken service slug redirects (Ahrefs 404 fixes - July 2026)
    // /services/agentic-ai/ → /services/gemini-agentic-ai/ (191 broken links across blog posts)
    if (path === "/services/agentic-ai" || path === "/services/agentic-ai/") {
      setLocation("/services/gemini-agentic-ai/");
      return;
    }
    // /services/google-ai-mode-optimization → /services/google-ai-mode-optimisation/ (US vs GB spelling)
    if (path === "/services/google-ai-mode-optimization" || path === "/services/google-ai-mode-optimization/") {
      setLocation("/services/google-ai-mode-optimisation/");
      return;
    }
    // /services/content-seo-intelligence → /services/seo-aeo-geo/
    if (path === "/services/content-seo-intelligence" || path === "/services/content-seo-intelligence/") {
      setLocation("/services/seo-aeo-geo/");
      return;
    }
    // /services/abm-account-based-marketing → /services/account-based-marketing/
    if (path === "/services/abm-account-based-marketing" || path === "/services/abm-account-based-marketing/") {
      setLocation("/services/account-based-marketing/");
      return;
    }

    // 11. Broken blog slug redirects (Ahrefs 404 fixes - July 2026)
    // Old/wrong slug versions → correct published slugs
    if (path === "/blog/performance-max-vs-smart-campaigns-b2b-2026" || path === "/blog/performance-max-vs-smart-campaigns-b2b-2026/") {
      setLocation("/blog/performance-max-vs-smart-campaigns-ai-bidding-strategy-2026");
      return;
    }
    if (path === "/blog/how-to-write-content-cited-in-chatgpt-answers" || path === "/blog/how-to-write-content-cited-in-chatgpt-answers/") {
      setLocation("/blog/write-content-cited-chatgpt-answers");
      return;
    }
    if (path === "/blog/aeo-2026-complete-guide" || path === "/blog/aeo-2026-complete-guide/") {
      setLocation("/blog/aeo-2026-complete-guide-answer-engine-optimisation-b2b/");
      return;
    }
    if (path === "/blog/aeo-complete-guide-answer-engine-optimization-b2b-2026" || path === "/blog/aeo-complete-guide-answer-engine-optimization-b2b-2026/") {
      setLocation("/blog/aeo-2026-complete-guide-answer-engine-optimisation-b2b/");
      return;
    }
    if (path === "/blog/multi-agent-ai-systems-automate-gtm-2026" || path === "/blog/multi-agent-ai-systems-automate-gtm-2026/") {
      setLocation("/blog/multi-agent-ai-automate-gtm-2026");
      return;
    }
    if (path === "/blog/competitors-ai-agents-targeting-your-accounts-2026" || path === "/blog/competitors-ai-agents-targeting-your-accounts-2026/") {
      setLocation("/blog/chatgpt-workspace-agents-b2b-gtm-dark-funnel-2026");
      return;
    }
    if (path === "/blog/ai-adoption-divide-gallup-mckinsey-gartner-2026" || path === "/blog/ai-adoption-divide-gallup-mckinsey-gartner-2026/") {
      setLocation("/blog/ai-adoption-divide-gallup-layoff-risk-marketing-teams-2026");
      return;
    }

    // 9. Individual blog posts
    const blogPostMatch = path.match(/^\/ai-marketing-aeo-geo-blog\/(.+)$/);
    if (blogPostMatch) {
      const oldSlug = blogPostMatch[1];
      const newSlug = BLOG_SLUG_MAP[oldSlug];
      if (newSlug !== undefined) {
        if (newSlug === "") {
          setLocation("/blog");
        } else {
          setLocation(`/blog/${newSlug}`);
        }
      } else {
        // Unknown slug, redirect to blog listing
        setLocation("/blog");
      }
      return;
    }
  }, [location, setLocation]);

  return null;
}
